import { useState } from "react";
import { apiPost } from "../lib/api";

/**
 * Self-service password change.
 *
 * Don Elwell (Alan Jay), 2026-08-06: "how can I get access to change my
 * password?" There was no answer — the app had a login form and nothing else, so
 * changes went through us generating one and emailing it in plaintext.
 *
 * Deliberately plain: three fields, one button, the server's own message on
 * failure. The confirm field is client-side only — it catches a typo before it
 * becomes a password nobody can reproduce, which the server cannot do for us.
 */
export function ChangePasswordDialog({ onClose }: { onClose: () => void }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (next !== confirm) {
      setError("The two new-password fields don't match.");
      return;
    }
    setBusy(true);
    try {
      await apiPost("/api/auth/change-password", {
        currentPassword: current,
        newPassword: next,
      });
      setDone(true);
    } catch (err) {
      // ApiError.message already carries the server's human-readable text.
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Change password"
    >
      <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl">
        {done ? (
          <>
            <h2 className="text-base font-semibold text-slate-900">
              Password changed
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              Your new password is active now. You'll stay signed in here — use
              the new one next time you sign in.
            </p>
            <button
              onClick={onClose}
              className="mt-4 w-full rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700"
            >
              Done
            </button>
          </>
        ) : (
          <form onSubmit={submit}>
            <h2 className="text-base font-semibold text-slate-900">
              Change password
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              At least 12 characters. You'll stay signed in.
            </p>
            <label className="mt-4 block text-xs font-medium text-slate-700">
              Current password
              <input
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                required
                className="mt-1 block w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm text-slate-900 focus:border-brand-500 focus:outline-none"
              />
            </label>
            <label className="mt-3 block text-xs font-medium text-slate-700">
              New password
              <input
                type="password"
                autoComplete="new-password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                required
                className="mt-1 block w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm text-slate-900 focus:border-brand-500 focus:outline-none"
              />
            </label>
            <label className="mt-3 block text-xs font-medium text-slate-700">
              New password again
              <input
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                className="mt-1 block w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm text-slate-900 focus:border-brand-500 focus:outline-none"
              />
            </label>
            {error && (
              <div className="mt-3 rounded-md bg-rose-50 px-2.5 py-2 text-xs text-rose-700">
                {error}
              </div>
            )}
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 rounded-md px-3 py-2 text-sm text-slate-600 hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy}
                className="flex-1 rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-60"
              >
                {busy ? "Saving…" : "Change password"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
